'use client';

import * as Slider from '@radix-ui/react-slider';
import { useState } from 'react';

import { Input } from '@eliadmin/core/src/components/ui/input';

export type Range = [number, number];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Typed number: spaces ignored, decimal comma or point; `null` while it is not a number. */
export function parseNumber(text: string) {
  const value = Number(text.replace(/\s/g, '').replace(',', '.'));
  return text.trim() && Number.isFinite(value) ? value : null;
}

export const formatNumber = (value: number) => String(value).replace('.', ',');

type RangeFilterProps = {
  min: number;
  max: number;
  value: Range;
  onChange: (value: Range) => void;
  /** Accessible names of the lower and the upper bound (input and slider thumb). */
  labels: [string, string];
  unit?: string;
  /** Slider value → input text and typed text → slider value (an index slider maps its values). */
  format?: (value: number) => string;
  parse?: (text: string) => number | null;
};

/**
 * Figma „Filter · Cena“: two 52 px inputs (48 on phones) with the unit in bold, joined by a dash,
 * and the dual range slider below them (4 px border/subtle track, bg/brand range, 20 px white
 * thumbs with a 2 px brand border). Typing moves the slider, dragging rewrites the inputs; both
 * stay within the bounds.
 */
export function RangeFilter({
  min,
  max,
  value: [from, to],
  onChange,
  labels,
  unit,
  format = formatNumber,
  parse = parseNumber,
}: RangeFilterProps) {
  const field = { unit, format, parse };
  const valueText = (value: number) => (unit ? `${format(value)} ${unit}` : format(value));

  return (
    <>
      <div className="flex items-center gap-2">
        <RangeInput
          {...field}
          label={labels[0]}
          value={from}
          onValue={value => onChange([clamp(value, min, to), to])}
        />
        <span aria-hidden="true" className="dlz-body-m text-[#6B7079]">
          –
        </span>
        <RangeInput
          {...field}
          label={labels[1]}
          value={to}
          onValue={value => onChange([from, clamp(value, from, max)])}
        />
      </div>
      <Slider.Root
        min={min}
        max={max}
        value={[from, to]}
        onValueChange={([lower, upper]) => onChange([lower, upper])}
        className="relative flex h-5 w-full touch-none select-none items-center"
      >
        <Slider.Track className="relative h-1 grow overflow-hidden rounded-full bg-[#E2E4E7]">
          <Slider.Range className="absolute h-full bg-[#D7191F]" />
        </Slider.Track>
        {[from, to].map((value, index) => (
          <Slider.Thumb
            key={index}
            aria-label={labels[index]}
            aria-valuetext={valueText(value)}
            className="block size-5 cursor-grab rounded-full border-2 border-[#D7191F] bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2 active:cursor-grabbing"
          />
        ))}
      </Slider.Root>
    </>
  );
}

function RangeInput({
  label,
  value,
  unit,
  format,
  parse,
  onValue,
}: {
  label: string;
  value: number;
  unit?: string;
  format: (value: number) => string;
  parse: (text: string) => number | null;
  onValue: (value: number) => void;
}) {
  // While focused the text stays as typed (clamping „1“ on the way to „150“ would fight the user).
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <Input
      aria-label={label}
      inputMode="decimal"
      autoComplete="off"
      value={draft ?? format(value)}
      onChange={event => {
        setDraft(event.target.value);
        const typed = parse(event.target.value);
        if (typed !== null) onValue(typed);
      }}
      onBlur={() => setDraft(null)}
      onKeyDown={event => {
        if (event.key === 'Enter') setDraft(null);
      }}
      after={
        unit ? (
          <span aria-hidden="true" className="font-bold">
            {unit}
          </span>
        ) : undefined
      }
      // Phones: 48 px with radius/sm 14 (Figma 60438:87794).
      className={{ wrapper: 'h-12 min-w-0 flex-1 rounded-[14px] md:h-[52px] md:rounded-[18px]' }}
    />
  );
}
